import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, GridLegacy as Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithFormStatusButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithFormStatusButtons';
import AppExercisePropertyAvatar from '../../../components/designe/app-entity-avatar/app-exercise-property-avatar/AppExercisePropertyAvatar';
import AppLanguageAvatar from '../../../components/designe/app-entity-avatar/app-language-avatar/AppExerciseAvatar';
import AppFileContainer from '../../../components/designe/app-file-container/AppFileContainer';
import AppSwitchWithLabel from '../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import AppFormChipSelector from '../../../components/designe/forms/app-form-chip-selector';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import AppHelpTooltipButton from '../../../components/designe/tooltips/AppHelpTooltipButton';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { AvatarSize, AvatarTypes } from '../../../shared/constants';
import { IExerciseLoadFormula } from '../../../types/interfaces/exercise-load-formula';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { ILanguage } from '../../../types/interfaces/language';
import { formatExerciseOptionDisplayName } from '../../../utils/exercise-option-display';
import { useStyles } from './NewExcercise.styles';
import { useNewExercisePage } from './useNewExercisePage';

function NewExerciseForm({
  exerciseId,
  onExerciseCreate,
  onCancelClick,
  onDirtyChange,
}: {
  exerciseId?: string;
  onExerciseCreate?: (exercise: string) => void;
  onCancelClick?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  const { back } = useNavigationStack();
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const leaveAction = () => (onCancelClick ? onCancelClick() : back());

  const {
    formik,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    exercise,
    exerciseOptions,
    categories,
    mainMuscleGroups,
    equipments,
    developedSkillOptions,
    onChangeFormikOption,
    languages,
    availableFormulas,
  } = useNewExercisePage({ exerciseId, onExerciseCreate });

  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction,
  });
  const isFormDirty = formik.dirty && !formik.isSubmitting;

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  useScrollToFirstError(formik, {
    fieldOrder: [
      'title',
      'avatarFileId',
      'mainMuscleGroup',
      'equipment',
      'category',
      'language',
    ],
  });

  function getPropertyItem(property: IExerciseProperty) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppExercisePropertyAvatar
          exerciseProperty={property}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">{property?.name}</Typography>
      </Box>
    );
  }

  function getLanguageItem(language: ILanguage) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppLanguageAvatar
          language={language}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">{language?.title}</Typography>
      </Box>
    );
  }

  return (
    <div className={classes.addExerciseWrapper}>
      {dialog}
      <form
        className={classes.addExerciseForm}
        action={async () => {
          await formik.submitForm();
        }}
        onSubmit={(e) => e.preventDefault()}>
        <AppFormTextField
          label={t('common.title')}
          fullWidth
          autoFocus
          name="title"
          className={classes.titleTextField}
          formik={formik}
        />
        <Box marginTop={2} data-formik-field="avatarFileId">
          <AppFileContainer
            files={exercise?.files}
            editMode={true}
            onFilesToDeleteChanged={onFilesToDeleteChanged}
            onFilesToUploadChanged={onFilesToUploadChanged}
            onMainFileChanged={(id: string | number) =>
              formik.setFieldValue('avatarFileId', id)
            }
            mainFileId={formik.values?.avatarFileId}
            uploadPreview={true}
          />
          {formik.errors?.['avatarFileId'] && (
            <Typography variant="body1" color="error">
              {t('formikValidation.exerciseAvatarRequired')}
            </Typography>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
          <AppFormSelectWrapper
            data-onboarding="newExercise.mainMuscleGroup"
            disableSorting
            disableClear
            label={t('exercises.mainMuscleGroup')}
            className={classes.selectorWithContent}
            options={mainMuscleGroups}
            name="mainMuscleGroup"
            formik={formik}
            renderOption={getPropertyItem}
          />

          <AppFormChipSelector
            maxTags={3}
            options={mainMuscleGroups?.filter(
              (muscle) => muscle.id !== formik.values?.mainMuscleGroup?.id,
            )}
            AvatarComponent={({ tag }) => (
              <AppExercisePropertyAvatar
                exerciseProperty={tag}
                size={AvatarSize.xs}
                type={AvatarTypes.round}
              />
            )}
            className={classes.selectorWithContent}
            name="additionalMuscles"
            formik={formik}
            fieldName="name"
            label={t('exercises.additionalMuscles')}
            itemWrapperCallback={getPropertyItem}
          />

          <AppFormSelectWrapper
            disableSorting
            disableClear
            label={t('exercises.equipment')}
            className={classes.selectorWithContent}
            options={equipments}
            name="equipment"
            formik={formik}
            renderOption={getPropertyItem}
          />

          <AppFormChipSelector
            maxTags={3}
            options={equipments?.filter(
              (eq) => eq.id !== formik.values?.equipment?.id,
            )}
            name="additionalEquipments"
            formik={formik}
            fieldName="name"
            AvatarComponent={({ tag }) => (
              <AppExercisePropertyAvatar
                exerciseProperty={tag}
                size={AvatarSize.xs}
                type={AvatarTypes.round}
              />
            )}
            label={t('exercises.additionalEquipments')}
            className={classes.selectorWithContent}
            itemWrapperCallback={getPropertyItem}
          />

          <AppFormChipSelector
            maxTags={5}
            options={developedSkillOptions}
            name="developedSkills"
            formik={formik}
            fieldName="name"
            AvatarComponent={({ tag }) => (
              <AppExercisePropertyAvatar
                exerciseProperty={tag}
                size={AvatarSize.xs}
                type={AvatarTypes.round}
              />
            )}
            label={t('exercises.developedSkills')}
            className={classes.selectorWithContent}
            itemWrapperCallback={getPropertyItem}
          />

          <AppFormSelectWrapper
            disableSorting
            disableClear
            className={classes.selectorWithContent}
            label={t('exercises.category')}
            options={categories}
            name="category"
            formik={formik}
            renderOption={getPropertyItem}
          />

          <AppFormSelectWrapper
            disableSorting
            label={t('exercises.language')}
            className={classes.selectorWithContent}
            options={languages}
            fieldName="title"
            disableSearch
            disableClear
            name="language"
            formik={formik}
            renderOption={getLanguageItem}
          />

          <Box className={classes.formulaFieldWrapper}>
            <Box className={classes.formulaSelect}>
              <AppFormSelectWrapper
                data-onboarding="newExercise.loadFormula"
                label={t('exercises.formulaForCalculationLoad')}
                className={classes.selectorWithContent}
                options={availableFormulas.map((f) => f.formula)}
                disabled={!availableFormulas.length}
                fieldName="name"
                disableSearch
                disableSorting
                name="exerciseLoadFormula"
                formik={formik}
                renderOption={(formula) => (
                  <Typography variant="text-md">
                    {formula.name || formula.nameKey}
                  </Typography>
                )}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const selectedFormula = e?.target
                    ?.value as unknown as IExerciseLoadFormula;

                  const selectedId = selectedFormula?.id
                    ? selectedFormula.id
                    : selectedFormula || null;
                  formik.setFieldValue('exerciseLoadFormula', selectedFormula);
                  formik.setFieldValue('exerciseLoadFormulaId', selectedId);
                }}
              />
            </Box>
            <AppHelpTooltipButton
              placement="top"
              scrollableContent
              content={
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="text-sm-sb">
                    {t('exercises.formulaForCalculationLoadHintTitle')}
                  </Typography>
                  <Typography variant="text-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    {t('exercises.formulaForCalculationLoadHint')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>
        <div
          className={classes.formItem}
          data-formik-field="options"
          data-onboarding="newExercise.options">
          <Typography variant={'text-sm'} className={classes.optionsLabel}>
            {t('exercises.options')}
          </Typography>
          <div className={classes.optionsContainer}>
            <Box className={classes.optionsGrid}>
              <Box className={classes.optionItem}>
                <Box data-onboarding="newExercise.splitBySide" width="100%">
                  <AppSwitchWithLabel
                    labelPlacement="end"
                    name="splitBySide"
                    label={t('exercises.splitBySide')}
                    value={formik.values?.splitBySide}
                    checked={formik.values?.splitBySide}
                    disabled={!!exercise?.id}
                    onChange={(e) =>
                      formik.setFieldValue('splitBySide', e.target.checked)
                    }
                  />
                </Box>
              </Box>
              {exerciseOptions?.map((option) => (
                <Box className={classes.optionItem} key={option.id}>
                  <AppSwitchWithLabel
                    labelPlacement="end"
                    name="options"
                    value={!!option.id}
                    disabled={!!exercise?.id}
                    label={formatExerciseOptionDisplayName(
                      option?.name || option.nameKey,
                      option.unit,
                      option.unitKey,
                    )}
                    checked={
                      !!formik.values?.options?.find((id) => id === option.id)
                    }
                    onChange={(e) =>
                      onChangeFormikOption(e, option)
                    }></AppSwitchWithLabel>
                </Box>
              ))}
            </Box>
          </div>
          {formik.errors?.['options'] && (
            <Typography variant="body1" color="error">
              {formik.errors?.['options']}
            </Typography>
          )}
        </div>

        <Box marginTop={2} data-onboarding="newExercise.description">
          <AppFormTextField
            label={t('common.description')}
            fullWidth
            multiline
            minRows={3}
            maxRows={15}
            name="description"
            formik={formik}
          />
        </Box>

        <div className={classes.buttonWrapper}>
          <AppBarWithFormStatusButtons
            isSubmitting={formik.isSubmitting}
            onSubmitClick={() => formik.submitForm()}
            onCancelClick={() => allowAndLeave()}
          />
        </div>
      </form>
    </div>
  );
}

export default NewExerciseForm;
